import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Json } from '@/integrations/supabase/types';

export interface InvoiceService {
  name: string;
  description?: string;
}

export interface TenantSettings {
  id: string;
  tenant_id: string;
  invoice_prefix: string | null;
  invoice_footer: string | null;
  default_roundoff: boolean | null;
  kiosk_withdraw_commission: number;
  kiosk_deposit_commission: number;
  kiosk_balance_commission: number;
  kiosk_mini_statement_commission: number;
  // Invoice customization
  gst_enabled: boolean | null;
  gst_number: string | null;
  cgst_percent: number | null;
  sgst_percent: number | null;
  print_size: 'thermal' | 'a4' | null;
  show_logo_on_invoice: boolean | null;
  terms_conditions: string | null;
  bank_details: string | null;
  upi_id: string | null;
  // Invoice asset fields
  qr_code_url: string | null;
  signature_url: string | null;
  stamp_enabled: boolean | null;
  invoice_services: InvoiceService[] | null;
  branding_text: string | null;
  branding_enabled: boolean | null;
  created_at: string;
  updated_at: string;
}

const DEFAULT_SETTINGS = {
  invoice_prefix: 'INV',
  invoice_footer: null,
  default_roundoff: true,
  kiosk_withdraw_commission: 0.5,
  kiosk_deposit_commission: 0.3,
  kiosk_balance_commission: 3,
  kiosk_mini_statement_commission: 5,
  gst_enabled: false,
  gst_number: null,
  cgst_percent: 9,
  sgst_percent: 9,
  print_size: 'a4' as const,
  show_logo_on_invoice: true,
  terms_conditions: null,
  bank_details: null,
  upi_id: null,
  qr_code_url: null,
  signature_url: null,
  stamp_enabled: true,
  invoice_services: [] as unknown as Json,
  branding_text: 'आपले सेवाकेंद्र - आपली सेवा | Your Service Center - Your Service',
  branding_enabled: true,
};

export function useTenantSettings() {
  const { tenant } = useAuth();
  const queryClient = useQueryClient();

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['tenant_settings', tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return null;

      // Try to fetch existing settings
      const { data, error } = await supabase
        .from('tenant_settings')
        .select('*')
        .eq('tenant_id', tenant.id)
        .maybeSingle();

      if (error) throw error;

      // If no settings exist, create default ones
      if (!data) {
        const { data: newSettings, error: insertError } = await supabase
          .from('tenant_settings')
          .insert({
            tenant_id: tenant.id,
            ...DEFAULT_SETTINGS,
          })
          .select()
          .single();

        if (insertError) throw insertError;
        
        return {
          ...newSettings,
          invoice_services: [] as InvoiceService[],
        } as TenantSettings;
      }

      // Parse invoice_services
      let parsedServices: InvoiceService[] = [];
      if (data.invoice_services) {
        try {
          parsedServices = Array.isArray(data.invoice_services) 
            ? data.invoice_services as unknown as InvoiceService[]
            : [];
        } catch {
          parsedServices = [];
        }
      }

      return {
        ...data,
        invoice_services: parsedServices,
      } as TenantSettings;
    },
    enabled: !!tenant?.id,
  });

  const updateSettings = useMutation({
    mutationFn: async (updates: Partial<Omit<TenantSettings, 'invoice_services'>> & { invoice_services?: Json }) => {
      if (!settings?.id) throw new Error('No settings found');

      const { error } = await supabase
        .from('tenant_settings')
        .update(updates)
        .eq('id', settings.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant_settings', tenant?.id] });
    },
  });

  // Helper to get commission rates
  const getCommissionRates = () => ({
    withdraw: settings?.kiosk_withdraw_commission ?? 0.5,
    deposit: settings?.kiosk_deposit_commission ?? 0.3,
    balance_inquiry: settings?.kiosk_balance_commission ?? 3,
    mini_statement: settings?.kiosk_mini_statement_commission ?? 5,
  });

  return {
    settings,
    isLoading,
    error,
    updateSettings,
    getCommissionRates,
  };
}
